<template>
    <v-dialog v-model="dialog" width="800">
      <v-card>
        <v-card-title class="headline">
          Dodaj ofertę
        </v-card-title>
        <v-card-text>
          <v-form
              ref="newForm"
              v-model="valid"
              lazy-validation
              class="buy-proposal--form mt-5 mb-5"
          >
            <v-row dense>
              <v-col cols="12" sm="6" md="4" align-self="start" dense>
                <VCurrencyField
                    ref="newFormPrice"
                    v-if="!isFreeUtilisationTransactionTypeChosen()"
                    v-model="form.price"
                    :rules="[priceRules]"
                    :max="maxPrice"
                    @focus="priceFocus"
                    @blur="priceChanged"
                    label="Cena"
                    suffix="PLN"
                    solo
                    class="wastemaster-input-elevation"
                ></VCurrencyField>
                <VCurrencyField
                    v-if="!isFreeUtilisationTransactionTypeChosen()"
                    v-model="form.pricePerUnit"
                    :rules="[pricePerUnitRules]"
                    :max="maxPrice"
                    :suffix="`PLN / ${unit}`"
                    @focus="pricePerUnitFocus"
                    @blur="pricePerUnitChanged"
                    label="Cena/Jednostka"
                    solo
                    class="wastemaster-input-elevation"
                ></VCurrencyField>
                <v-select
                    :items="getTransportConditions()"
                    :rules="formRules.transportConditions"
                    v-model="form.transportCondition"
                    label="Transport"
                    item-value="key"
                    item-text="value"
                    solo
                    class="wastemaster-input-elevation"
                ></v-select>
                <v-select
                    :items="partners"
                    v-model="form.partner"
                    label="Partner"
                    item-value="id"
                    item-text="name"
                    solo
                    class="wastemaster-input-elevation"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="4" align-self="start" dense>
                <v-select
                    :items="getTransactionTypes()"
                    :rules="formRules.transactionTypes"
                    v-model="form.transactionType"
                    @change="onBuyOptionChanged"
                    label="Rodzaj transakcji"
                    item-value="key"
                    item-text="value"
                    solo
                    class="wastemaster-input-elevation"
                ></v-select>
                <v-select
                    v-if="!isFreeUtilisationTransactionTypeChosen()"
                    :items="getPriceTypes()"
                    v-model="form.isPriceNet"
                    label="Brutto / Netto"
                    item-value="isNet"
                    item-text="label"
                    solo
                    class="wastemaster-input-elevation"
                ></v-select>
                <v-select
                    :items="getPaymentConditions()"
                    :rules="formRules.paymentConditions"
                    v-if="!isFreeUtilisationTransactionTypeChosen()"
                    v-model="form.paymentCondition"
                    label="Warunki płatności"
                    item-value="key"
                    item-text="value"
                    solo
                    class="wastemaster-input-elevation"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="4" align-self="start" dense>
                <v-menu
                    ref="validUntilMenu"
                    v-model="validUntilMenu"
                    :close-on-content-click="true"
                    transition="scale-transition"
                    offset-y
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                        v-on="on"
                        v-model="form.validUntil"
                        label="Ważność oferty"
                        readonly
                        class="mt-0 pt-0 wastemaster-input-elevation"
                        solo
                    ></v-text-field>
                  </template>
                  <v-date-picker
                      v-model="form.validUntil"
                      :min="getFormattedDate(0)"
                      :first-day-of-week="1"
                      locale="pl"
                      color="success"
                      no-title
                      scrollable
                  >
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="8" align-self="start" dense>
                <div
                    v-if="isAddressVisible"
                    class="text-left buy-proposal--address-label"
                >
                  Miejsce dostawy odpadu
                </div>
                <AddressForm
                    v-if="isAddressVisible"
                    v-model="form.address"
                    :allRequired="isAddressRequired()"
                    solo
                ></AddressForm>
              </v-col>
              <v-col cols="12" md="8" align-self="start" dense>
                <v-textarea
                    v-model="form.notes"
                    label="Uwagi"
                    solo
                    class="wastemaster-input-elevation"
                ></v-textarea>
              </v-col>
            </v-row>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                  @click.prevent="onSubmit"
                  type="Submit"
                  class="action-btn--green-gradient place-btn wastemaster-action-btn text-none"
                  large
              >Wyślij&nbsp;<v-icon size="24">mdi-send</v-icon></v-btn
              >
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
</template>

<script>

import api from "@/services/api";
import { VCurrencyField } from 'v-currency-field'
import transactionTypeMixin from "@/mixins/transactionTypeMixin";
import transportConditionMixin from "@/mixins/transportConditionMixin";
import categoryMixin from "@/mixins/categoryMixin";
import priceMixin from "@/mixins/priceMixin";
import paymentConditionMixin from "@/mixins/paymentConditionMixin";
import dateMixin from "@/mixins/dateMixin";
import validationHelpers from '@/mixins/validationHelpers'
import AddressForm from '@/components/AddressForm'

export default {
  name: 'ModalAddBuyProposal',
  components: { AddressForm, VCurrencyField },
  props: {
    item: {},
  },
  mixins: [
      transactionTypeMixin,
      transportConditionMixin,
      categoryMixin,
      priceMixin,
      paymentConditionMixin,
      dateMixin,
      validationHelpers
  ],
  created() {
    this.loadPartners();
    this.loadUnit();
  },
  data: () => ({
    partners: [],
    unit: "",
    dialog: false,
    maxPrice: 999999999,
    validUntilMenu: false,
    form: {
      price: undefined,
      pricePerUnit: undefined,
      isPriceNet: true,
      transactionType: null,
      partner: null,
      validUntil: undefined,
      notes: '',
      paymentCondition: null,
      transportCondition: null,
      address: {
        city: '',
        postalCode: '',
        region: ''
      }
    },
    formRules: {
      transactionTypes: [(value) => !!value || 'Wybierz typ transakcji'],
      paymentConditions: [(value) => !!value || 'Wybierz warunki płatności'],
      transportConditions: [(value) => !!value || 'Wybierz warunki transportu']
    },
    valid: false,
  }),
  methods: {
    open() {
      this.dialog = true
    },
    isFreeUtilisationTransactionTypeChosen() {
      return this.isFreeUtilisationTransactionType(this.form.transactionType)
    },
    priceRules() {
      if (!this.form.price || this.form.price <= 0) {
        return 'Wpisz cenę większą od 0.00'
      }

      return true
    },
    pricePerUnitRules() {
      if (this.form.pricePerUnit === null || this.form.pricePerUnit < 0) {
        return 'Wpisz cenę większą lub równą 0.00'
      }

      return true
    },
    priceFocus() {
      this.onPriceEntryFieldValue = this.form.price
    },
    pricePerUnitFocus() {
      this.onPricePerUnitEntryFieldValue = this.form.pricePerUnit
    },
    priceChanged() {
      if (this.onPriceEntryFieldValue !== this.form.price) {
        this.form.pricePerUnit = this.calculatePricePerUnit(this.form.price)
      }
      this.onPriceEntryFieldValue = undefined
    },
    calculatePricePerUnit(price) {
      return parseFloat(price / this.item.quantity + '').toFixed(2)
    },
    pricePerUnitChanged() {
      if (this.onPricePerUnitEntryFieldValue !== this.form.pricePerUnit) {
        this.form.price = this.form.pricePerUnit * this.item.quantity
      }
      this.onPricePerUnitEntryFieldValue = undefined
    },
    onBuyOptionChanged() {
      if (this.isFreeUtilisationTransactionTypeChosen()) {
        this.clearFormPrice()
      }
    },
    clearFormPrice() {
      this.form.price = 0
      this.form.pricePerUnit = 0
    },
    isAddressRequired() {
      return this.isLocoBuyerTransportCondition(this.form.transportCondition)
    },
    isAddressVisible() {
      return !this.isLocoSellerTransportCondition(this.form.transportCondition)
    },
    async onSubmit() {
      if (!this.$refs.newForm.validate()) {
        return
      }

      const buyProposal = {
        saleOfferId: this.item.id,
        price: this.form.price,
        type: this.form.transactionType,
        postalCode: this.form.address.postalCode
            ? this.form.address.postalCode
            : undefined,
        city: this.form.address.city ? this.form.address.city : undefined,
        region: this.form.address.region ? this.form.address.region : undefined,
        notes: this.form.notes,
        payment: this.form.paymentCondition,
        transport: this.form.transportCondition,
        validUntil: this.form.validUntil,
        isPriceNet: this.form.isPriceNet,
        partner: this.form.partner,
      }
      api
          .post('/api/buy-proposals',buyProposal)
          .then(() => {
            this.$root.$emit('notifySuccess', {
              html: 'Oferta została dodana.',
            });
            this.dialog = false;
            this.$parent.loadBuyProposals();
          })
          .catch((error) => {
            console.error('Error when adding a buy proposal:', error);
            this.$root.$emit('notifyError', {
              html: 'Wystąpił błąd.',
            });
          })
    },
    async loadPartners() {
      api
          .get('/api/companies?partner=true')
          .then((data) => {
            this.partners = data.data.items;
          })
          .catch((error) => {
            console.error('Error when loading partners: ', error);
          })
    },
    async loadUnit() {
      api
          .get('/api/categories')
          .then((data) => {
            this.unit = data.data.categories.find(el => el.id == this.item.categoryId).unit;
          })
          .catch((error) => {
            console.error('Error when loading categories: ', error);
          })
    },
  },
}
</script>

<style scoped></style>
